import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  Contractor,
  ContractorContact,
  ContractorFilters
} from '../types/contractor.ts';
import { ContractorCalculator, contractorUtils } from '../utils/contractorCalculations.ts';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  Star,
  MapPin,
  Phone,
  Mail,
  Users,
  AlertTriangle,
  CheckCircle,
  Building2,
  UserPlus,
  ChevronDown,
  ChevronRight,
  Settings,
  X
} from 'lucide-react';

const ContractorManagement: React.FC = () => {
  const { currentUser } = useAuth();
  
  // State management
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [filteredContractors, setFilteredContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedContractors, setExpandedContractors] = useState<Set<string>>(new Set());
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showCustomFieldsModal, setShowCustomFieldsModal] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
  
  // Form state for contact
  const [contactFormData, setContactFormData] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    mobile: '',
    department: '',
    notes: ''
  });

  // Form state for contractor
  const [contractorFormData, setContractorFormData] = useState({
    companyName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    predefinedMarkup: '4.00' as '4.00' | '4.50' | '25%' | 'custom',
    customMarkupValue: '',
    customMarkupType: 'fixed' as 'fixed' | 'percentage',
    invoicePrefix: '', // e.g., 'BTC', 'REL' - leave empty for no prefix (like CEC)
    paymentTerms: 'net30' as 'net15' | 'net30' | 'net45' | 'immediate' | 'weekly' | 'biweekly'
  });

  // Form state for editing contractor
  const [editContractorFormData, setEditContractorFormData] = useState({
    companyName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    predefinedMarkup: '4.00' as '4.00' | '4.50' | '25%' | 'custom',
    customMarkupValue: '',
    customMarkupType: 'fixed' as 'fixed' | 'percentage',
    invoicePrefix: '', // e.g., 'BTC', 'REL' - leave empty for no prefix (like CEC)
    paymentTerms: 'net30' as 'net15' | 'net30' | 'net45' | 'immediate' | 'weekly' | 'biweekly'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Custom fields form state
  const [customFieldsFormData, setCustomFieldsFormData] = useState<{
    [fieldKey: string]: {
      label: string;
      type: 'text' | 'number' | 'date' | 'boolean' | 'select';
      required: boolean;
      placeholder?: string;
      helpText?: string;
      category?: string;
      options?: string[];
      order?: number;
    };
  }>({});
  
  const [newFieldKey, setNewFieldKey] = useState('');
  const [editingFieldKey, setEditingFieldKey] = useState<string | null>(null);
  
  // Filter and search state
  const [filters, setFilters] = useState<ContractorFilters>({
    search: '',
    status: 'all',
    specialization: '',
    availability: undefined,
    rating: undefined,
    location: '',
    hasValidLicense: false,
    hasValidInsurance: false,
    clientId: ''
  });
  
  // Statistics
  const contractorStats = useMemo(() => {
    return ContractorCalculator.calculateContractorStats(contractors);
  }, [contractors]);
  
  // Load data
  useEffect(() => {
    loadContractors();
  }, []);
  
  // Filter contractors
  useEffect(() => {
    filterContractors();
  }, [contractors, filters]);
  
  const loadContractors = async () => {
    try {
      setLoading(true);
      const contractorsRef = collection(db, 'contractors');
      const snapshot = await getDocs(contractorsRef);
      
      const contractorData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        licenseExpires: doc.data().licenseExpires?.toDate(),
        insuranceExpires: doc.data().insuranceExpires?.toDate(),
        bondExpires: doc.data().bondExpires?.toDate(),
        lastLoginAt: doc.data().lastLoginAt?.toDate()
      })) as Contractor[];
      
      setContractors(contractorData);
    } catch (err) {
      console.error('Error loading contractors:', err);
      setError('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };
  
  const filterContractors = () => {
    let filtered = [...contractors];
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(contractor =>
        contractor.companyName.toLowerCase().includes(searchLower) ||
        contractor.primaryContact.name.toLowerCase().includes(searchLower) ||
        contractor.primaryContact.email.toLowerCase().includes(searchLower) ||
        contractor.contacts.some(contact => 
          contact.name.toLowerCase().includes(searchLower) ||
          contact.email.toLowerCase().includes(searchLower)
        )
      );
    }
    
    // Status filter
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(contractor => contractor.status === filters.status);
    }
    
    
    setFilteredContractors(filtered);
  };

  const handleAddContact = async () => {
    if (!selectedContractor) return;

    try {
      const newContact: ContractorContact = {
        id: Date.now().toString(),
        name: contactFormData.name,
        title: contactFormData.title,
        email: contactFormData.email,
        phone: contactFormData.phone,
        mobile: contactFormData.mobile,
        isPrimary: false,
        isActive: true,
        department: contactFormData.department,
        notes: contactFormData.notes
      };

      const updatedContacts = [...selectedContractor.contacts, newContact];
      
      await updateDoc(doc(db, 'contractors', selectedContractor.id), {
        contacts: updatedContacts,
        updatedAt: new Date()
      });

      setShowAddContactModal(false);
      setContactFormData({
        name: '',
        title: '',
        email: '',
        phone: '',
        mobile: '',
        department: '',
        notes: ''
      });
      loadContractors();
      setError(null);
    } catch (err) {
      console.error('Error adding contact:', err);
      setError('Failed to add contact');
    }
  };

  const handleEditContractor = (contractor: Contractor) => {
    // Populate edit form with contractor data
    const markupValue = contractor.billing.markupValue || 0;
    const markupType = contractor.billing.markupType || 'fixed';
    const predefinedMarkup = contractor.billing.predefinedMarkup || '4.00';
    
    let customMarkupValue = '';
    let customMarkupType: 'fixed' | 'percentage' = 'fixed';
    
    if (predefinedMarkup === 'custom') {
      customMarkupValue = markupValue.toString();
      customMarkupType = markupType === 'percentage' ? 'percentage' : 'fixed';
    }

    setEditContractorFormData({
      companyName: contractor.companyName,
      street: contractor.address.street,
      city: contractor.address.city,
      state: contractor.address.state,
      zipCode: contractor.address.zipCode,
      contactName: contractor.primaryContact.name,
      contactTitle: contractor.primaryContact.title,
      contactEmail: contractor.primaryContact.email,
      contactPhone: contractor.primaryContact.phone,
      predefinedMarkup: predefinedMarkup,
      customMarkupValue: customMarkupValue,
      customMarkupType: customMarkupType,
      invoicePrefix: contractor.invoicePrefix || '',
      paymentTerms: contractor.billing.paymentTerms || 'net30'
    });

    setSelectedContractor(contractor);
    setShowEditModal(true);
  };

  const handleUpdateContractor = async () => {
    if (!selectedContractor) return;
    
    // Form validation
    if (!editContractorFormData.companyName.trim()) {
      setError('Company name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create updated primary contact
      const updatedPrimaryContact: ContractorContact = {
        ...selectedContractor.primaryContact,
        name: editContractorFormData.contactName || 'Main Contact',
        title: editContractorFormData.contactTitle || '',
        email: editContractorFormData.contactEmail || '',
        phone: editContractorFormData.contactPhone || ''
      };

      // Calculate markup values
      let markupType: 'fixed' | 'percentage';
      let markupValue: number;
      
      if (editContractorFormData.predefinedMarkup === 'custom') {
        markupType = editContractorFormData.customMarkupType;
        markupValue = parseFloat(editContractorFormData.customMarkupValue || '0');
      } else if (editContractorFormData.predefinedMarkup === '25%') {
        markupType = 'percentage';
        markupValue = 25;
      } else {
        markupType = 'fixed';
        markupValue = parseFloat(editContractorFormData.predefinedMarkup);
      }

      // Update contractor in Firestore
      await updateDoc(doc(db, 'contractors', selectedContractor.id), {
        companyName: editContractorFormData.companyName.trim(),
        address: {
          street: editContractorFormData.street || '',
          city: editContractorFormData.city || '',
          state: editContractorFormData.state || '',
          zipCode: editContractorFormData.zipCode || ''
        },
        primaryContact: updatedPrimaryContact,
        billing: {
          ...selectedContractor.billing,
          markupType: markupType,
          markupValue: markupValue,
          predefinedMarkup: editContractorFormData.predefinedMarkup,
          paymentTerms: editContractorFormData.paymentTerms
        },
        invoicePrefix: editContractorFormData.invoicePrefix.trim().toUpperCase() || undefined,
        updatedAt: Timestamp.now()
      });

      // Reset form and close modal
      setEditContractorFormData({
        companyName: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        contactName: '',
        contactTitle: '',
        contactEmail: '',
        contactPhone: '',
        predefinedMarkup: '4.00',
        customMarkupValue: '',
        customMarkupType: 'fixed',
        invoicePrefix: '',
        paymentTerms: 'net30'
      });
      setShowEditModal(false);
      setSelectedContractor(null);
      
      // Reload contractors list
      loadContractors();
      
    } catch (err) {
      console.error('Error updating contractor:', err);
      setError('Failed to update contractor. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddContractor = async () => {
    // Form validation
    if (!contractorFormData.companyName.trim()) {
      setError('Company name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create primary contact object
      const primaryContact: ContractorContact = {
        id: Date.now().toString(),
        name: contractorFormData.contactName || 'Main Contact',
        title: contractorFormData.contactTitle || '',
        email: contractorFormData.contactEmail || '',
        phone: contractorFormData.contactPhone || '',
        mobile: '',
        isPrimary: true,
        isActive: true,
        department: '',
        notes: ''
      };

      // Create new contractor object
      const newContractor = {
        companyName: contractorFormData.companyName.trim(),
        businessType: 'llc' as const,
        address: {
          street: contractorFormData.street || '',
          city: contractorFormData.city || '',
          state: contractorFormData.state || '',
          zipCode: contractorFormData.zipCode || ''
        },
        primaryContact: primaryContact,
        contacts: [],
        taxId: '',
        licenseNumber: '',
        licenseExpires: null,
        insuranceExpires: null,
        bondAmount: 0,
        bondExpires: null,
        specializations: [],
        certifications: [],
        performance: {
          totalJobsCompleted: 0,
          totalJobsAssigned: 0,
          completionRate: 0,
          onTimeCompletionRate: 0,
          averageQualityScore: 0,
          reworkRate: 0,
          customerSatisfactionScore: 0,
          averageJobDuration: 0,
          productivityScore: 0,
          utilizationRate: 0,
          totalEarnings: 0,
          averageJobValue: 0,
          profitabilityScore: 0,
          totalHoursWorked: 0,
          averageHoursPerJob: 0,
          overtimeHours: 0,
          recentMetrics: {
            jobsCompleted: 0,
            averageRating: 0,
            onTimeRate: 0,
            hoursWorked: 0
          },
          monthlyPerformance: []
        },
        billing: {
          defaultHourlyRate: 0,
          overtimeRate: 1.5,
          holidayRate: 2.0,
          markupType: contractorFormData.predefinedMarkup === 'custom' 
            ? contractorFormData.customMarkupType
            : contractorFormData.predefinedMarkup === '25%' 
              ? 'percentage' as const 
              : 'fixed' as const,
          markupValue: contractorFormData.predefinedMarkup === 'custom' 
            ? parseFloat(contractorFormData.customMarkupValue || '0') 
            : contractorFormData.predefinedMarkup === '25%' 
              ? 25 
              : parseFloat(contractorFormData.predefinedMarkup),
          predefinedMarkup: contractorFormData.predefinedMarkup,
          clientRates: {},
          paymentTerms: contractorFormData.paymentTerms,
          paymentMethod: 'check' as const,
          taxClassification: '1099' as const,
          taxWithholding: 0,
          totalBilled: 0,
          totalPaid: 0,
          outstandingBalance: 0,
          invoiceDeliveryMethod: 'email' as const,
          invoiceFormat: 'standard' as const
        },
        contractTerms: {
          contractType: 'ongoing' as const,
          startDate: new Date(),
          autoRenewal: false,
          exclusivityRequired: false,
          nonCompeteClause: false,
          liabilityInsuranceRequired: false,
          workersCompRequired: false,
          bondRequired: false,
          toolsProvided: 'contractor' as const,
          vehicleRequired: false,
          uniformRequired: false,
          qualityStandards: [],
          safetyRequirements: [],
          terminationNotice: 30,
          terminationReasons: []
        },
        status: 'active' as const,
        availability: {
          workingHours: {
            monday: { available: true, startTime: '08:00', endTime: '17:00' },
            tuesday: { available: true, startTime: '08:00', endTime: '17:00' },
            wednesday: { available: true, startTime: '08:00', endTime: '17:00' },
            thursday: { available: true, startTime: '08:00', endTime: '17:00' },
            friday: { available: true, startTime: '08:00', endTime: '17:00' },
            saturday: { available: false },
            sunday: { available: false }
          },
          currentStatus: 'available' as const,
          maxJobsPerWeek: 5,
          maxHoursPerWeek: 40,
          currentJobCount: 0,
          currentHoursScheduled: 0,
          serviceAreas: [],
          travelRadius: 50
        },
        documents: [],
        ratings: [],
        averageRating: 0,
        communicationPreferences: {
          email: true,
          sms: false,
          phone: true,
          preferredMethod: 'email' as const,
          timezone: 'America/New_York'
        },
        invoicePrefix: contractorFormData.invoicePrefix.trim().toUpperCase() || undefined,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: currentUser?.uid || ''
      };

      // Save to Firestore
      await addDoc(collection(db, 'contractors'), newContractor);

      // Reset form and close modal
      setContractorFormData({
        companyName: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        contactName: '',
        contactTitle: '',
        contactEmail: '',
        contactPhone: '',
        predefinedMarkup: '4.00',
        customMarkupValue: '',
        customMarkupType: 'fixed',
        invoicePrefix: ''
      });
      setShowAddModal(false);
      
      // Reload contractors list
      loadContractors();
      
    } catch (err) {
      console.error('Error adding contractor:', err);
      setError('Failed to add contractor. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManageCustomFields = (contractor: Contractor) => {
    setSelectedContractor(contractor);
    setCustomFieldsFormData(contractor.customEmployeeFields || {});
    setShowCustomFieldsModal(true);
  };

  const handleAddCustomField = () => {
    if (!newFieldKey.trim()) return;
    
    const fieldKey = newFieldKey.toLowerCase().replace(/\s+/g, '_');
    if (customFieldsFormData[fieldKey]) return; // Field already exists
    
    setCustomFieldsFormData({
      ...customFieldsFormData,
      [fieldKey]: {
        label: newFieldKey,
        type: 'text',
        required: false,
        category: 'General',
        order: Object.keys(customFieldsFormData).length
      }
    });
    
    setNewFieldKey('');
  };

  const handleUpdateCustomField = (fieldKey: string, updates: Partial<typeof customFieldsFormData[string]>) => {
    setCustomFieldsFormData({
      ...customFieldsFormData,
      [fieldKey]: {
        ...customFieldsFormData[fieldKey],
        ...updates
      }
    });
  };

  const handleRemoveCustomField = (fieldKey: string) => {
    const newFields = { ...customFieldsFormData };
    delete newFields[fieldKey];
    setCustomFieldsFormData(newFields);
  };

  const handleSaveCustomFields = async () => {
    if (!selectedContractor) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await updateDoc(doc(db, 'contractors', selectedContractor.id), {
        customEmployeeFields: customFieldsFormData,
        updatedAt: new Date()
      });
      
      setShowCustomFieldsModal(false);
      setCustomFieldsFormData({});
      setSelectedContractor(null);
      loadContractors();
    } catch (err) {
      console.error('Error saving custom fields:', err);
      setError('Failed to save custom fields');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleContractorExpansion = (contractorId: string) => {
    const newExpanded = new Set(expandedContractors);
    if (newExpanded.has(contractorId)) {
      newExpanded.delete(contractorId);
    } else {
      newExpanded.add(contractorId);
    }
    setExpandedContractors(newExpanded);
  };

  const formatMarkupDisplay = (contractor: Contractor) => {
    const { markupType, markupValue, predefinedMarkup } = contractor.billing;
    
    if (!markupValue && markupValue !== 0) {
      return 'No markup set';
    }

    if (predefinedMarkup && predefinedMarkup !== 'custom') {
      if (predefinedMarkup === '25%') {
        return '25%';
      }
      return `$${predefinedMarkup}`;
    }

    if (markupType === 'percentage') {
      return `${markupValue}%`;
    } else {
      return `$${markupValue.toFixed(2)}`;
    }
  };
  
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-brand-gold rounded-2xl flex items-center justify-center mb-4 mx-auto animate-pulse">
            <Building2 className="w-8 h-8 text-brand-dark" />
          </div>
          <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading contractors...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Contractor Management</h1>
              <p className="text-gray-600 mt-2">Manage contractor companies and their contacts</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-brand-gold text-brand-dark px-6 py-3 rounded-xl font-semibold hover:bg-yellow-500 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Contractor</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
              <p className="text-red-800">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                ×
              </button>
            </div>
          </div>
        )}
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Contractors</p>
                <p className="text-3xl font-bold text-gray-900">{contractorStats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Contractors</p>
                <p className="text-3xl font-bold text-gray-900">{contractorStats.active}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Contacts</p>
                <p className="text-3xl font-bold text-gray-900">
                  {contractors.reduce((sum, c) => sum + c.contacts.length + 1, 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search contractors and contacts..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="terminated">Terminated</option>
              </select>
              
            </div>
          </div>
        </div>
        
        {/* Contractors List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {filteredContractors.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No contractors found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding a new contractor company.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-brand-gold text-brand-dark px-4 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors"
                >
                  Add Contractor
                </button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredContractors.map((contractor) => (
                <div key={contractor.id} className="p-6">
                  {/* Contractor Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => toggleContractorExpansion(contractor.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {expandedContractors.has(contractor.id) ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </button>
                      
                      <div className="w-12 h-12 bg-brand-gold rounded-xl flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-brand-dark" />
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{contractor.companyName}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {contractor.address.city}, {contractor.address.state}
                          </span>
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {contractor.contacts.length + 1} contacts
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">
                          {formatMarkupDisplay(contractor)}
                        </div>
                        <div className="text-sm text-gray-500">Markup</div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">
                          {contractor.invoicePrefix ? contractor.invoicePrefix + '-####' : '####'}
                        </div>
                        <div className="text-sm text-gray-500">Invoice Format</div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedContractor(contractor);
                            setShowAddContactModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50"
                          title="Add Contact"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleManageCustomFields(contractor)}
                          className="text-purple-600 hover:text-purple-900 p-2 rounded-lg hover:bg-purple-50"
                          title="Manage Custom Employee Fields"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditContractor(contractor)}
                          className="text-yellow-600 hover:text-yellow-900 p-2 rounded-lg hover:bg-yellow-50"
                          title="Edit Contractor"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Contacts */}
                  {expandedContractors.has(contractor.id) && (
                    <div className="mt-6 ml-9 space-y-3">
                      {/* Primary Contact */}
                      <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <Users className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {contractor.primaryContact.name}
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  PRIMARY
                                </span>
                              </div>
                              <div className="text-sm text-gray-600">{contractor.primaryContact.title}</div>
                            </div>
                          </div>
                          <div className="text-right text-sm text-gray-600">
                            <div className="flex items-center">
                              <Mail className="w-4 h-4 mr-1" />
                              {contractor.primaryContact.email}
                            </div>
                            <div className="flex items-center mt-1">
                              <Phone className="w-4 h-4 mr-1" />
                              {contractor.primaryContact.phone}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Additional Contacts */}
                      {contractor.contacts.map((contact) => (
                        <div key={contact.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <Users className="w-4 h-4 text-gray-600" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{contact.name}</div>
                                <div className="text-sm text-gray-600">{contact.title}</div>
                                {contact.department && (
                                  <div className="text-xs text-gray-500">{contact.department}</div>
                                )}
                              </div>
                            </div>
                            <div className="text-right text-sm text-gray-600">
                              <div className="flex items-center">
                                <Mail className="w-4 h-4 mr-1" />
                                {contact.email}
                              </div>
                              <div className="flex items-center mt-1">
                                <Phone className="w-4 h-4 mr-1" />
                                {contact.phone}
                              </div>
                              {contact.mobile && (
                                <div className="flex items-center mt-1">
                                  <Phone className="w-4 h-4 mr-1" />
                                  {contact.mobile} (mobile)
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Contact Modal */}
        {showAddContactModal && selectedContractor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Add Contact to {selectedContractor.companyName}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={contactFormData.name}
                    onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={contactFormData.title}
                    onChange={(e) => setContactFormData({ ...contactFormData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={contactFormData.phone}
                    onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddContactModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddContact}
                  className="bg-brand-gold text-brand-dark px-4 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors"
                >
                  Add Contact
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Contractor Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Contractor</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    placeholder="Enter company name"
                    value={contractorFormData.companyName}
                    onChange={(e) => setContractorFormData({ ...contractorFormData, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Address</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        placeholder="Enter street address"
                        value={contractorFormData.street}
                        onChange={(e) => setContractorFormData({ ...contractorFormData, street: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          placeholder="City"
                          value={contractorFormData.city}
                          onChange={(e) => setContractorFormData({ ...contractorFormData, city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          placeholder="State"
                          value={contractorFormData.state}
                          onChange={(e) => setContractorFormData({ ...contractorFormData, state: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                        <input
                          type="text"
                          placeholder="ZIP"
                          value={contractorFormData.zipCode}
                          onChange={(e) => setContractorFormData({ ...contractorFormData, zipCode: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Primary Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                      <input
                        type="text"
                        placeholder="Contact person name"
                        value={contractorFormData.contactName}
                        onChange={(e) => setContractorFormData({ ...contractorFormData, contactName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        placeholder="Job title"
                        value={contractorFormData.contactTitle}
                        onChange={(e) => setContractorFormData({ ...contractorFormData, contactTitle: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        placeholder="email@company.com"
                        value={contractorFormData.contactEmail}
                        onChange={(e) => setContractorFormData({ ...contractorFormData, contactEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={contractorFormData.contactPhone}
                        onChange={(e) => setContractorFormData({ ...contractorFormData, contactPhone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Markup Configuration</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Markup Type</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['4.00', '4.50', '25%', 'custom'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setContractorFormData({ 
                              ...contractorFormData, 
                              predefinedMarkup: option as '4.00' | '4.50' | '25%' | 'custom',
                              customMarkupValue: option !== 'custom' ? '' : contractorFormData.customMarkupValue
                            })}
                            className={`px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all ${
                              contractorFormData.predefinedMarkup === option
                                ? 'border-brand-gold bg-brand-gold text-brand-dark'
                                : 'border-gray-200 text-gray-600 hover:border-brand-gold hover:text-brand-gold'
                            }`}
                          >
                            {option === 'custom' ? 'Custom' : option === '25%' ? '25%' : `$${option}`}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Fixed amounts (4.00, 4.50) are added per hour. Percentage (25%) is applied to base rate.
                      </p>
                    </div>
                    
                    {contractorFormData.predefinedMarkup === 'custom' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Markup Type</label>
                          <div className="flex gap-3">
                            <button
                              type="button"
                              onClick={() => setContractorFormData({ 
                                ...contractorFormData, 
                                customMarkupType: 'fixed'
                              })}
                              className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                                contractorFormData.customMarkupType === 'fixed'
                                  ? 'border-brand-gold bg-brand-gold text-brand-dark'
                                  : 'border-gray-200 text-gray-600 hover:border-brand-gold hover:text-brand-gold'
                              }`}
                            >
                              Dollar Amount
                            </button>
                            <button
                              type="button"
                              onClick={() => setContractorFormData({ 
                                ...contractorFormData, 
                                customMarkupType: 'percentage'
                              })}
                              className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                                contractorFormData.customMarkupType === 'percentage'
                                  ? 'border-brand-gold bg-brand-gold text-brand-dark'
                                  : 'border-gray-200 text-gray-600 hover:border-brand-gold hover:text-brand-gold'
                              }`}
                            >
                              Percentage
                            </button>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Custom Markup {contractorFormData.customMarkupType === 'fixed' ? 'Amount' : 'Percentage'}
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                              {contractorFormData.customMarkupType === 'fixed' ? '$' : '%'}
                            </span>
                            <input
                              type="number"
                              step={contractorFormData.customMarkupType === 'fixed' ? '0.01' : '1'}
                              min="0"
                              max={contractorFormData.customMarkupType === 'percentage' ? '100' : undefined}
                              placeholder={contractorFormData.customMarkupType === 'fixed' ? '0.00' : '0'}
                              value={contractorFormData.customMarkupValue}
                              onChange={(e) => setContractorFormData({ 
                                ...contractorFormData, 
                                customMarkupValue: e.target.value 
                              })}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {contractorFormData.customMarkupType === 'fixed' 
                              ? 'Enter custom markup amount per hour (e.g., 3.75)'
                              : 'Enter percentage markup (e.g., 15 for 15%)'
                            }
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice Configuration */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Configuration</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Prefix (Optional)</label>
                    <input
                      type="text"
                      placeholder="e.g., BTC, REL (leave empty for no prefix like CEC)"
                      value={contractorFormData.invoicePrefix}
                      onChange={(e) => setContractorFormData({ 
                        ...contractorFormData, 
                        invoicePrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 5)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      maxLength={5}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This prefix will be used for invoice numbering (e.g., "BTC-1001"). Leave empty for plain numbers like CEC invoices.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                    <select
                      value={contractorFormData.paymentTerms}
                      onChange={(e) => setContractorFormData({ 
                        ...contractorFormData, 
                        paymentTerms: e.target.value as 'net15' | 'net30' | 'net45' | 'immediate' | 'weekly' | 'biweekly'
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    >
                      <option value="net15">Net 15 Days</option>
                      <option value="net30">Net 30 Days</option>
                      <option value="net45">Net 45 Days</option>
                      <option value="immediate">Due Immediately</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Payment terms that will be displayed on invoices for this contractor.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-8">
                <button
                  onClick={() => setShowAddModal(false)}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddContractor}
                  disabled={isSubmitting || !contractorFormData.companyName.trim()}
                  className="bg-brand-gold text-brand-dark px-6 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-dark mr-2"></div>
                  )}
                  {isSubmitting ? 'Adding...' : 'Add Contractor'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Contractor Modal */}
        {showEditModal && selectedContractor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Contractor</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    placeholder="Enter company name"
                    value={editContractorFormData.companyName}
                    onChange={(e) => setEditContractorFormData({ ...editContractorFormData, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Address</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        placeholder="Enter street address"
                        value={editContractorFormData.street}
                        onChange={(e) => setEditContractorFormData({ ...editContractorFormData, street: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          placeholder="City"
                          value={editContractorFormData.city}
                          onChange={(e) => setEditContractorFormData({ ...editContractorFormData, city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          placeholder="State"
                          value={editContractorFormData.state}
                          onChange={(e) => setEditContractorFormData({ ...editContractorFormData, state: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                        <input
                          type="text"
                          placeholder="ZIP"
                          value={editContractorFormData.zipCode}
                          onChange={(e) => setEditContractorFormData({ ...editContractorFormData, zipCode: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Primary Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                      <input
                        type="text"
                        placeholder="Contact person name"
                        value={editContractorFormData.contactName}
                        onChange={(e) => setEditContractorFormData({ ...editContractorFormData, contactName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        placeholder="Job title"
                        value={editContractorFormData.contactTitle}
                        onChange={(e) => setEditContractorFormData({ ...editContractorFormData, contactTitle: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        placeholder="email@company.com"
                        value={editContractorFormData.contactEmail}
                        onChange={(e) => setEditContractorFormData({ ...editContractorFormData, contactEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={editContractorFormData.contactPhone}
                        onChange={(e) => setEditContractorFormData({ ...editContractorFormData, contactPhone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Markup Configuration</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Markup Type</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['4.00', '4.50', '25%', 'custom'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setEditContractorFormData({ 
                              ...editContractorFormData, 
                              predefinedMarkup: option as '4.00' | '4.50' | '25%' | 'custom',
                              customMarkupValue: option !== 'custom' ? '' : editContractorFormData.customMarkupValue
                            })}
                            className={`px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all ${
                              editContractorFormData.predefinedMarkup === option
                                ? 'border-brand-gold bg-brand-gold text-brand-dark'
                                : 'border-gray-200 text-gray-600 hover:border-brand-gold hover:text-brand-gold'
                            }`}
                          >
                            {option === 'custom' ? 'Custom' : option === '25%' ? '25%' : `$${option}`}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Fixed amounts (4.00, 4.50) are added per hour. Percentage (25%) is applied to base rate.
                      </p>
                    </div>
                    
                    {editContractorFormData.predefinedMarkup === 'custom' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Markup Type</label>
                          <div className="flex gap-3">
                            <button
                              type="button"
                              onClick={() => setEditContractorFormData({ 
                                ...editContractorFormData, 
                                customMarkupType: 'fixed'
                              })}
                              className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                                editContractorFormData.customMarkupType === 'fixed'
                                  ? 'border-brand-gold bg-brand-gold text-brand-dark'
                                  : 'border-gray-200 text-gray-600 hover:border-brand-gold hover:text-brand-gold'
                              }`}
                            >
                              Dollar Amount
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditContractorFormData({ 
                                ...editContractorFormData, 
                                customMarkupType: 'percentage'
                              })}
                              className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                                editContractorFormData.customMarkupType === 'percentage'
                                  ? 'border-brand-gold bg-brand-gold text-brand-dark'
                                  : 'border-gray-200 text-gray-600 hover:border-brand-gold hover:text-brand-gold'
                              }`}
                            >
                              Percentage
                            </button>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Custom Markup {editContractorFormData.customMarkupType === 'fixed' ? 'Amount' : 'Percentage'}
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                              {editContractorFormData.customMarkupType === 'fixed' ? '$' : '%'}
                            </span>
                            <input
                              type="number"
                              step={editContractorFormData.customMarkupType === 'fixed' ? '0.01' : '1'}
                              min="0"
                              max={editContractorFormData.customMarkupType === 'percentage' ? '100' : undefined}
                              placeholder={editContractorFormData.customMarkupType === 'fixed' ? '0.00' : '0'}
                              value={editContractorFormData.customMarkupValue}
                              onChange={(e) => setEditContractorFormData({ 
                                ...editContractorFormData, 
                                customMarkupValue: e.target.value 
                              })}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {editContractorFormData.customMarkupType === 'fixed' 
                              ? 'Enter custom markup amount per hour (e.g., 3.75)'
                              : 'Enter percentage markup (e.g., 15 for 15%)'
                            }
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice Configuration */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Configuration</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Prefix (Optional)</label>
                    <input
                      type="text"
                      placeholder="e.g., BTC, REL (leave empty for no prefix like CEC)"
                      value={editContractorFormData.invoicePrefix}
                      onChange={(e) => setEditContractorFormData({ 
                        ...editContractorFormData, 
                        invoicePrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 5)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      maxLength={5}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This prefix will be used for invoice numbering (e.g., "BTC-1001"). Leave empty for plain numbers like CEC invoices.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                    <select
                      value={editContractorFormData.paymentTerms}
                      onChange={(e) => setEditContractorFormData({ 
                        ...editContractorFormData, 
                        paymentTerms: e.target.value as 'net15' | 'net30' | 'net45' | 'immediate' | 'weekly' | 'biweekly'
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    >
                      <option value="net15">Net 15 Days</option>
                      <option value="net30">Net 30 Days</option>
                      <option value="net45">Net 45 Days</option>
                      <option value="immediate">Due Immediately</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Payment terms that will be displayed on invoices for this contractor.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-8">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateContractor}
                  disabled={isSubmitting || !editContractorFormData.companyName.trim()}
                  className="bg-brand-gold text-brand-dark px-6 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-dark mr-2"></div>
                  )}
                  {isSubmitting ? 'Updating...' : 'Update Contractor'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Fields Modal */}
        {showCustomFieldsModal && selectedContractor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Custom Employee Fields - {selectedContractor.companyName}
                </h2>
                <button
                  onClick={() => setShowCustomFieldsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Add New Field */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Add New Field</h3>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      placeholder="Field name (e.g., Employee Number)"
                      value={newFieldKey}
                      onChange={(e) => setNewFieldKey(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    />
                    <button
                      onClick={handleAddCustomField}
                      className="bg-brand-gold text-brand-dark px-4 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors"
                    >
                      Add Field
                    </button>
                  </div>
                </div>

                {/* Existing Fields */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Custom Fields</h3>
                  {Object.keys(customFieldsFormData).length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No custom fields configured. Add a field above to get started.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(customFieldsFormData).map(([fieldKey, fieldConfig]) => (
                        <div key={fieldKey} className="border rounded-lg p-4 space-y-3">
                          <div className="flex justify-between items-center">
                            <h4 className="font-medium text-gray-900">{fieldConfig.label}</h4>
                            <button
                              onClick={() => handleRemoveCustomField(fieldKey)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Remove field"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                              <input
                                type="text"
                                value={fieldConfig.label}
                                onChange={(e) => handleUpdateCustomField(fieldKey, { label: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                              <select
                                value={fieldConfig.type}
                                onChange={(e) => handleUpdateCustomField(fieldKey, { type: e.target.value as any })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="date">Date</option>
                                <option value="boolean">Yes/No</option>
                                <option value="select">Dropdown</option>
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                              <select
                                value={fieldConfig.category || 'General'}
                                onChange={(e) => handleUpdateCustomField(fieldKey, { category: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                              >
                                <option value="General">General</option>
                                <option value="Training">Training</option>
                                <option value="Certification">Certification</option>
                                <option value="Equipment">Equipment</option>
                                <option value="Safety">Safety</option>
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
                              <input
                                type="text"
                                value={fieldConfig.placeholder || ''}
                                onChange={(e) => handleUpdateCustomField(fieldKey, { placeholder: e.target.value })}
                                placeholder="Optional placeholder text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Help Text</label>
                              <input
                                type="text"
                                value={fieldConfig.helpText || ''}
                                onChange={(e) => handleUpdateCustomField(fieldKey, { helpText: e.target.value })}
                                placeholder="Optional help text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                              />
                            </div>
                            
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`required-${fieldKey}`}
                                checked={fieldConfig.required}
                                onChange={(e) => handleUpdateCustomField(fieldKey, { required: e.target.checked })}
                                className="mr-2"
                              />
                              <label htmlFor={`required-${fieldKey}`} className="text-sm font-medium text-gray-700">
                                Required Field
                              </label>
                            </div>
                          </div>
                          
                          {/* Dropdown options */}
                          {fieldConfig.type === 'select' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Dropdown Options</label>
                              <textarea
                                value={(fieldConfig.options || []).join('\n')}
                                onChange={(e) => handleUpdateCustomField(fieldKey, { 
                                  options: e.target.value.split('\n').filter(opt => opt.trim()) 
                                })}
                                placeholder="Enter each option on a new line"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-8">
                <button
                  onClick={() => setShowCustomFieldsModal(false)}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCustomFields}
                  disabled={isSubmitting}
                  className="bg-brand-gold text-brand-dark px-6 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-dark mr-2"></div>
                  )}
                  {isSubmitting ? 'Saving...' : 'Save Fields'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractorManagement;
